import React, { useEffect, useRef } from 'react';
import { SessionParticipant, SessionTranscriptEntry } from '../../types/superadmin';
import { EmptyState } from '../primitives';
import { isSalesperson, speakerClass, speakerLabel, transcriptTime } from '../../lib/speakers';

/**
 * The call as it was spoken: salesperson on the right, customers on the left,
 * each customer in their own colour slot so a multi-party call stays readable.
 *
 * `scrollToTimestamp` is how rubric evidence lands here — the caller switches
 * to this tab and passes the evidence's ISO stamp; the matching line scrolls
 * into view and flashes.
 */

interface Props {
  transcripts: SessionTranscriptEntry[];
  participants: SessionParticipant[];
  /** ISO timestamp of the line to reveal, or null. */
  scrollToTimestamp?: string | null;
}

/** Roster legend, so "Customer 2" is attached to a colour before you scroll. */
function ParticipantLegend({ participants }: { participants: SessionParticipant[] }) {
  if (participants.length === 0) return null;
  return (
    <div className="tx-legend">
      {participants.map((participant, index) => {
        const asEntry: SessionTranscriptEntry = {
          text: '',
          speaker: participant.role,
          participantId: participant.participantId ?? null,
          speakerName: participant.name ?? null,
          timestamp: null
        };
        return (
          <span key={String(participant.participantId ?? index)} className="tx-legend-item">
            <span className={'swatch ' + speakerClass(asEntry, participants)} aria-hidden />
            {participant.name || speakerLabel(asEntry, participants)}
            <span className="caption"> · {participant.role}</span>
          </span>
        );
      })}
    </div>
  );
}

export default function SessionTranscript({ transcripts, participants, scrollToTimestamp }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  // The target line is addressed by `data-ts` rather than by index: evidence
  // carries a timestamp, and the transcript can be re-ordered or filtered
  // without invalidating it.
  useEffect(() => {
    if (!scrollToTimestamp || !containerRef.current) return;
    const target = containerRef.current.querySelector<HTMLElement>(
      `[data-ts="${CSS.escape(scrollToTimestamp)}"]`
    );
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('flash');
    const timerId = window.setTimeout(() => target.classList.remove('flash'), 2200);
    return () => window.clearTimeout(timerId);
  }, [scrollToTimestamp, transcripts]);

  if (!transcripts || transcripts.length === 0) {
    return <EmptyState>This session has no transcript.</EmptyState>;
  }

  return (
    <div className="transcript" ref={containerRef}>
      <ParticipantLegend participants={participants} />
      {transcripts.map((entry, index) => {
        const mine = isSalesperson(entry);
        return (
          <div
            key={index}
            data-ts={entry.timestamp || undefined}
            className={'tx-row' + (mine ? ' mine' : '')}
          >
            <div className="tx-col">
              <div className="tx-meta">
                <span className="who">{speakerLabel(entry, participants)}</span>
                <span className="mono when">{transcriptTime(entry.timestamp)}</span>
              </div>
              <div className={'tx-bubble ' + speakerClass(entry, participants)}>{entry.text}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
